import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

export type ViewMode = 'simple' | 'split'
/** Inbox grouping (design 13a). */
export type InboxGroupBy = 'dueDate' | 'none'
/** Next 7 days as day groups or as 7 columns (design 34a). */
export type UpcomingLayout = 'list' | 'week'
/** Row density for task pages (design 13b). */
export type ListDensity = 'comfortable' | 'compact'
/** Optional row fields, toggled under Display → Show on rows (design 25b). */
export type RowField = 'id' | 'priority' | 'due' | 'description'
export const ROW_FIELDS: ReadonlyArray<RowField> = ['id', 'priority', 'due', 'description']

interface ViewModeStateValues {
  viewMode: ViewMode
  inboxGroupBy: InboxGroupBy
  upcomingLayout: UpcomingLayout
  listDensity: ListDensity
  hiddenRowFields: Array<RowField>
  /** Display → Show sub-tasks: flatten sub-tasks into lists as rows (design 28b). */
  showSubTasks: boolean
}

interface ViewModeState extends ViewModeStateValues {
  setViewMode: (mode: ViewMode) => void
  toggleViewMode: () => void
  setInboxGroupBy: (groupBy: InboxGroupBy) => void
  setUpcomingLayout: (layout: UpcomingLayout) => void
  setListDensity: (density: ListDensity) => void
  toggleRowField: (field: RowField) => void
  setShowSubTasks: (show: boolean) => void
  resetDisplay: () => void
}

const initialState: ViewModeStateValues = {
  viewMode: 'simple',
  inboxGroupBy: 'dueDate',
  upcomingLayout: 'list',
  listDensity: 'comfortable',
  hiddenRowFields: [],
  showSubTasks: false,
}

const useViewModeStore = create<ViewModeState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setViewMode: (mode) => {
          set(() => ({
            viewMode: mode,
          }))
        },
        setInboxGroupBy: (groupBy) => set({ inboxGroupBy: groupBy }),
        setUpcomingLayout: (layout) => set({ upcomingLayout: layout }),
        setListDensity: (density) => set({ listDensity: density }),
        toggleRowField: (field) =>
          set((state) => ({
            hiddenRowFields: state.hiddenRowFields.includes(field)
              ? state.hiddenRowFields.filter((f) => f !== field)
              : [...state.hiddenRowFields, field],
          })),
        setShowSubTasks: (show) => set({ showSubTasks: show }),
        // Display → Reset: arrangement and row fields, not how tasks open.
        resetDisplay: () =>
          set({
            inboxGroupBy: initialState.inboxGroupBy,
            listDensity: initialState.listDensity,
            hiddenRowFields: [],
            showSubTasks: initialState.showSubTasks,
          }),
        toggleViewMode: () => {
          const currentMode = get().viewMode
          set(() => ({
            viewMode: currentMode === 'simple' ? 'split' : 'simple',
          }))
        },
      }),
      {
        name: 'moly-view-mode',
        storage: createJSONStorage(() => localStorage),
      },
    ),
  ),
)

export default useViewModeStore
